import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { controlValueIsSameValidator } from '../../common/validators/control-value-is-same';
import { UsersService } from '../users.service';
import { Observable } from 'rxjs';
import { EmailSubscription, User } from '@deckplan.io/common';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { filter, map, switchMap } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { AlertLevel, AlertsService } from '../../common/alerts/alerts.service';
import { StripeService } from 'ngx-stripe';
import { StripeCardElement, StripeCardElementChangeEvent, StripeCardElementOptions, StripeElements, StripeElementsOptions } from '@stripe/stripe-js';
import { DeckplanStripeService } from '../deckplan-stripe.service';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Theme, ThemeService } from '../../common/theme/theme.service';
import { environment } from '../../../environments/environment';
import { EmailSubscriptionService } from '../../moderation/email-subscription.service';

const HTTP_UNAUTH_CODE = 401;
const PASSWORD_MIN_LENGTH = 8;

@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss']
})
export class UserProfileComponent implements OnInit {

    public user$: Observable<User>;
    public selectedUserId: number;
    public userHasSubscription = false;

    public emailSubscriptionsChange: FormGroup;
    public passwordChange: FormGroup;
    public subscriptionChange: FormGroup;

    public possibleEmailSubscriptions: EmailSubscription[] = [];
    public emailSubscriptionsControls: FormArray;

    public showCardFields = false;
    public showConfirmCancellation = false;

    public subscriptionLevel: string;
    public stripeLast4: string;
    public stripeExpMonth: number;
    public stripeExpYear: number;

    public themeIsLightColor: boolean;

    public cardOptions: StripeCardElementOptions = {
        style: {
            base: {
                iconColor: '#666EE8',
                color: '#31325F',
                fontWeight: '300',
                fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                fontSize: '18px',
                '::placeholder': {
                    color: '#CFD7E0'
                }
            }
        }
    };

    public elementsOptions: StripeElementsOptions = {
        locale: 'en'
    };

    public elements: StripeElements;
    public card: StripeCardElement;
    public cardError: string;
    public stripeError: string;

    private user: User;

    constructor(formBuilder: FormBuilder,
                private readonly usersService: UsersService,
                public readonly authService: AuthService,
                private readonly alertsService: AlertsService,
                private readonly stripeService: StripeService,
                private readonly deckplanStripeService: DeckplanStripeService,
                private readonly emailSubscriptionService: EmailSubscriptionService,
                private readonly themes: ThemeService,
                private readonly route: ActivatedRoute) {
        this.emailSubscriptionsChange = formBuilder.group({
            emailSubscriptions: formBuilder.array([])
        });

        this.emailSubscriptionsControls = this.emailSubscriptionsChange.get('emailSubscriptions') as FormArray;

        this.passwordChange = formBuilder.group({
            currentPass: ['', [Validators.required, Validators.minLength(PASSWORD_MIN_LENGTH)]],
            newPass: ['', Validators.required],
            confirmNewPass: ['', Validators.required]
        });

        this.passwordChange.get('confirmNewPass').setValidators([
            Validators.required,
            Validators.minLength(PASSWORD_MIN_LENGTH),
            controlValueIsSameValidator(this.passwordChange.get('newPass'))
        ]);

        this.subscriptionChange = formBuilder.group({
            fullName: ['', Validators.required]
        });
    }

    public ngOnInit(): void {
        this.user$ = this.route.paramMap.pipe(
            filter((params: ParamMap) => params.get('id') !== null),
            map((params: ParamMap) => this.selectedUserId = +params.get('id')),
            switchMap(id => this.usersService.getOne(id))
        );

        this.user$.subscribe((user) => {
            this.updateUserData(user);

            // Pull all of the email subscriptions we know about and then create form controls for them;
            // mark individual controls checked if the user has its corresponding subscription object
            this.emailSubscriptionService.getAll().subscribe((emailSubscriptions) => {
                this.possibleEmailSubscriptions = emailSubscriptions;
                this.emailSubscriptionsControls.controls = emailSubscriptions.map((emailSubscription) => new FormControl(this.userIsSubscribedToEmail(emailSubscription)));
            });
        });

        this.themes.themeChanges.subscribe(this.updateForTheme.bind(this));
    }

    private updateForTheme(theme: Theme) {
        // We could simply set this to 'theme === Theme.Light' but I want to make it
        // easy to adapt this to additional theme options later.
        if (theme === Theme.Light) {
            this.themeIsLightColor = true;
        } else if (theme === Theme.Dark) {
            this.themeIsLightColor = false;
        }
    }

    private updateUserData(user: User) {
        this.userHasSubscription = user.subscriptionStatus === 'active';
        this.subscriptionLevel = this.userHasSubscription ? 'Planner' : 'Free';
        this.user = user;
        this.stripeLast4 = user.subscriptionPaymentLastFour;
        this.stripeExpMonth = user.subscriptionPaymentExpirationMonth;
        this.stripeExpYear = user.subscriptionPaymentExpirationYear;
    }

    private userIsSubscribedToEmail(emailSubscription: EmailSubscription): boolean {
        for (const userEmailSubscription of this.user.emailSubscriptions) {
            if (userEmailSubscription.name === emailSubscription.name) {
                return true;
            }
        }

        return false;
    }

    public onUpdateEmailPreferencesSubmit(isNoEmails: boolean) {
        const successAlertHandler = () => {
            this.alertsService.alert('Successfully updated email subscriptions', AlertLevel.InfoSuccess);
            this.updateUser();
        };

        const errorAlertHandler = (err) => {
            if (err.status === 0) {
                this.alertsService.alert('We couldn\'t reach the server!', AlertLevel.InfoFailure);
            } else if (err.status === HTTP_UNAUTH_CODE) {
                this.alertsService.alert('Unable to change email subscriptions, you are not authorized to change these!', AlertLevel.InfoFailure);
            } else {
                this.alertsService.alert('An unknown error has occurred! Please try again later', AlertLevel.InfoFailure);
            }
            this.updateUser();
        };

        if (isNoEmails) {
            this.usersService.update(this.selectedUserId, {emailSubscriptions: []})
                .subscribe(successAlertHandler, errorAlertHandler);
            return;
        }

        const newSubscriptions: EmailSubscription[] = [];
        for (let i = 0; i < this.emailSubscriptionsControls.controls.length; i++) {
            const control = this.emailSubscriptionsControls.controls[i];
            if (control.value) {
                newSubscriptions.push(this.possibleEmailSubscriptions[i]);
            }
        }

        this.usersService.update(this.selectedUserId, {emailSubscriptions: newSubscriptions})
            .subscribe(successAlertHandler, errorAlertHandler);
    }

    private updateUser() {
        this.usersService.getOne(this.selectedUserId)
            .subscribe((user) => {
                this.updateUserData(user);

                for (let i = 0; i < this.possibleEmailSubscriptions.length; i++) {
                    this.emailSubscriptionsControls.controls[i].setValue(this.userIsSubscribedToEmail(this.possibleEmailSubscriptions[i]));
                }
            });
    }

    public onChangePasswordSubmit() {
        if (!this.passwordChange.valid) {
            return;
        }

        const newPassword = this.passwordChange.get('confirmNewPass').value;

        if (this.authService.currentUserValue.isAdmin) {
            this.changePassword(newPassword);
        } else {
            // Verify that the current password is correct
            const currentPassword = this.passwordChange.get('currentPass').value;
            this.authService.login(this.authService.currentUserValue.email, currentPassword)
                .subscribe(
                    () => this.changePassword(newPassword),
                    err => {
                        if (err.status === 0) {
                            this.alertsService.alert('We couldn\'t reach the server!', AlertLevel.InfoFailure);
                        } else if (err.status === HTTP_UNAUTH_CODE) {
                            this.alertsService.alert('Unable to change password, current password incorrect!', AlertLevel.InfoFailure);
                        } else {
                            this.alertsService.alert('An unknown error has occurred! Please try again later', AlertLevel.InfoFailure);
                        }
                    }
                );
        }
    }

    private changePassword(newPassword: string) {
        this.usersService.update(this.selectedUserId, {password: newPassword})
            .subscribe(() => this.alertsService.alert('Successfully changed password!', AlertLevel.InfoSuccess),
                err => {
                    if (err.status === 0) {
                        this.alertsService.alert('We couldn\'t reach the server!', AlertLevel.InfoFailure);
                    } else if (err.status === HTTP_UNAUTH_CODE) {
                        this.alertsService.alert('Unable to change password, you are not authorized to change this password!', AlertLevel.InfoFailure);
                    } else {
                        this.alertsService.alert('An unknown error has occurred! Please try again later', AlertLevel.InfoFailure);
                    }
                });
    }

    public setShowCardFields(showFields: boolean) {
        this.showCardFields = showFields;
        if (showFields) {
            this.subscriptionChange.get('fullName').enable();
            // The element we're trying to mount to doesn't exist yet, by the time this method is called,
            // which makes Stripe angry. Therefore, set a zero-timer to run the card element mounting immediately
            // after the render loop concludes
            setTimeout(() => { this.mountCardElement(); }, 0);
        } else {
            this.subscriptionChange.get('fullName').disable();
        }
    }

    private mountCardElement(): void {
        this.stripeService.elements(this.elementsOptions)
            .subscribe(elements => {
                this.elements = elements;
                // Only create the element the first time
                if (!this.card) {
                    this.card = this.elements.create('card', this.cardOptions);
                    this.card.on('change', changeEvent => this.handleCardError(changeEvent));
                }

                // We need to mount it every time we come in here, since we're using an ngIf to control if the fields
                // are visible or not, it gets unmounted with the parent container is removed by the if.
                this.card.mount('#cardElement');
            });
    }

    public handleCardError(event: StripeCardElementChangeEvent): void {
        if (event.error) {
            this.cardError = event.error.message;
        } else {
            this.cardError = '';
        }
    }

    public handleStripeError(error: string): void {
        if (error) {
            this.stripeError = error;
        } else {
            this.stripeError = '';
        }
    }

    public handleSubscriptionChangeSuccess() {
        this.alertsService.alert(this.userHasSubscription ? 'Card information updated successfully!' : 'Successfully subscribed!', AlertLevel.InfoSuccess);
        this.userHasSubscription = true;
        this.subscriptionLevel = 'Planner';
        this.setShowCardFields(false);
    }

    public subscriptionChangeSubmit() {
        if (!this.subscriptionChange.valid) {
            this.alertsService.alert('Something was wrong in the signup form', AlertLevel.InfoFailure);
            return;
        }

        // Handle the updated details
        this.createPaymentMethod(this.user.customerId, this.user.id);
    }

    public cancelSubscription() {
        this.deckplanStripeService.cancelSubscription(this.selectedUserId)
            .subscribe(
                () => {
                    this.alertsService.alert('Subscription canceled. We\'re sorry to see you go!', AlertLevel.InfoSuccess);
                },
                err => {
                    if (err.status === 0) {
                        this.alertsService.alert('We couldn\'t reach the server!', AlertLevel.InfoFailure);
                    } else if (err.status === HTTP_UNAUTH_CODE) {
                        this.alertsService.alert('Unable to cancel subscription, you are not authorized to cancel it.', AlertLevel.InfoFailure);
                    } else {
                        this.alertsService.alert('An unknown error has occurred! Please try again later', AlertLevel.InfoFailure);
                    }
                }
            );
    }

    public onTabChange(event: MatTabChangeEvent) {
        // We don't want to try to mount the stripe elements until we have the mounting location on the page.
        if (event.tab.textLabel === 'Subscription') {
            this.setShowCardFields(!this.userHasSubscription);
        }
    }

    private createPaymentMethod(customerId: string, userId: number) {
        const billingName = this.subscriptionChange.controls.fullName.value;

        // If we're at this point, then they've selected paid, so set up the billing stuff
        this.stripeService.createPaymentMethod({
            type: 'card',
            card: this.card,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            billing_details: {
                name: billingName
            }
        }).subscribe((result) => {
            if (result.error) {
                this.handleStripeError(result.error.message);
            } else {
                this.createSubscription(customerId, result.paymentMethod.id, environment.stripePriceId, userId);
            }
        });
    }

    private createSubscription(customerId: string, paymentMethodId: string, priceId: string, userId: number): void {
        const subscriptionHandler = (subscription: any) => {
            if (subscription && subscription.status === 'active') {
                this.handleSubscriptionChangeSuccess();
                return;
            }

            // If we've gotten down here, then we need to handle additional payment details (e.g. 3DSecure)
            this.handleAdditionalPaymentAction(subscription, paymentMethodId, userId);
        };

        const errorHandler = (error) => {
            this.handleStripeError(error);
        };

        if (!this.userHasSubscription) {
            this.deckplanStripeService.createSubscription(customerId, paymentMethodId, priceId, userId)
                .subscribe(subscriptionHandler, errorHandler);
        } else {
            this.deckplanStripeService.updatePaymentMethod(userId, paymentMethodId)
                .subscribe(subscriptionHandler, errorHandler);
        }
    }

    private handleAdditionalPaymentAction(subscription: any, paymentMethodId: string, userId: number) {
        const paymentIntent = subscription.latest_invoice.payment_intent;
        if (paymentIntent.status === 'requires_action') {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            this.stripeService.confirmCardPayment(paymentIntent.client_secret, { payment_method: paymentMethodId })
                .subscribe((result) => {
                    if (result.error) {
                        this.handleStripeError(result.error.message);
                    } else if (result.paymentIntent.status === 'succeeded') {
                        this.handleSubscriptionChangeSuccess();
                    }
                });
        } else {
            this.handleSubscriptionChangeSuccess();
        }
    }
}
